import { Fragment, useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { onboardingRecordQuery, isTransientReadFailure } from './onboardingRecordQuery';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Lock } from 'lucide-react';
import {
  ONBOARDING_TRUSTED_MEMBER_KEY, ONBOARDING_TRUSTED_MEMBER_NAME_KEY, ONBOARDING_TRUSTED_CHOICE_KEYS, adultChildIndexes, trustedSpouseName, resolveTrustedContact, trustedMemberValue, parseTrustedMember,
  ONBOARDING_FORM_FIELDS, ONBOARDING_SENT_AT_KEY, ONBOARDING_TO_UNIFIED, clientFormSectionsFor, ONBOARDING_STRING_MAX,
  CLIENT_FORM_CLOSURE_COPY, cleanInfoAnswer, mergeOnboardingPrefill, unifiedQuestionById, clientStaffMark, clientSsnReplacedNote,
  type OnboardingFieldSpec, type OnboardingFormSection, type OnboardingClientFormSection,
} from '@steward-app/steward-types';
import { ApiError, userMessage } from '../../api/client';
import {
  clearOnboardingTaxId, getOnboardingForm, onboardingFormQueryKey, saveOnboardingSection,
  setOnboardingTaxId, type OnboardingFormView, type OnboardingIdentityView,
} from '../../api/onboardingForm';
import {
  getOnboardingRecord, isAlreadySent, isRecordNotReady, sendOnboardingRecord, type OnboardingRecordView,
} from '../../api/onboardingRecord';
import { baseOf, filledInKeys, saveRecord, sectionPayload, staffKeysOf, valuesToWire, wireToValues, type RecordBase } from './onboardingRecord';
import { firmDisplayName } from '../../intake/IntakeBrandScope';
import { ClientStaffNotice } from '../../intake/StaffEnteredNotes';
import { markDate } from '../../intake/staffMarks';
import { counterText } from '../../intake/fields';
import { InfoRequestsStep, useInfoRequestUploads } from './InfoRequestsStep';
import { REQUEST_CLOSED_NOTE, type NumberFlush } from './AccountNumberCard';
import { answeredCount, filledNumberIds, requestTitle, reviewNumberValue, splitRequests, storedAnswer, withAnswer } from './infoRequestsModel';
import { UserText } from '../../components/UserText';
import { InvitedEmail } from '../../components/InvitedEmail';
import { cls as formCls } from '../../components/formStyles';
import { FormFooter } from '../../components/form-shell/FormFooter';
import { FirmWordmark } from '../../components/form-shell/FirmWordmark';
import { FormShell } from '../../components/form-shell/FormShell';
import type { SaveStatus } from '../../components/form-shell/SaveIndicator';
import { SectionCard } from '../../components/form-shell/SectionCard';
import { SectionIndicator } from '../../components/form-shell/SectionIndicator';
import { ONBOARDING_MARK_WORDS, type RailSection } from '../../components/form-shell/types';
import { missingFor, reviewGroup, ssnNeededSentence, SPOUSE_SSN_MISSING, NOT_FILLED_IN, type AnswerPage, type Page, type Values } from './reviewAnswers';
import { btns } from '../../components/buttons';
import { fmtDate } from '../../lib/formatDate';
import { LeaveControl, OnboardingBackToAppContext, OnboardingCompleted } from './OnboardingClientScreens';
import { useContext } from 'react';
import { RequestsPage } from './RequestsPage';
import { onboardingSectionStatus } from '../onboardingFormStatus';
import { useTesterFeedbackScreen } from '../../testerfeedback/screen';

/**
 * The client's onboarding form at `/onboarding/form` (crossmarc/steward-app#1765, mock
 * `OnboardingForm.dc.html`). The welcome page says: "{firm} has asked you to fill in a few
 * details so they can open your accounts. It takes about 15 minutes. Your answers save as you
 * go. You can stop and come back anytime." This file keeps what the code does behind that:
 *
 *   - **Saves as you go**: the form autosaves a moment after the last keystroke, and on
 *     Continue, Back or a nav click. One save at a time, in order. Since docs/43 slice 4c
 *     (crossmarc/steward-app#2699) the answers live on the unified client record, and a save
 *     sends the WHOLE form (appcore replaces every question placed on the onboarding form, so an
 *     erased box is erased, and a question the page does not draw is carried through unchanged;
 *     see `onboardingRecord.ts`). The form opens on the record, so an answer the client already
 *     gave in the snapshot is shown here and saves back to the same question. A save the record
 *     finds stale (another tab or device saved first) is merged, never replaced: the client's
 *     unsaved edits stay on the page. The information-request answers, the tax id, account numbers
 *     and uploads stay on the `/me/onboarding-forms/...` routes.
 *   - **Send happens once**: the record stamps the first send; a second is the server's no-op,
 *     which this form reads as sent. The client's place in the form is kept in this browser only.
 *   - **Come back anytime**: what was saved is what loads next time. Whether the firm can read
 *     a save is the firm-side route's business (`GET /advisor/client-records/{clientId}/onboarding`),
 *     not something this file promises the client, and the welcome page no longer does.
 *     No client-facing line on this form now claims when the firm sees a save: the Review
 *     page's "{firm} sees them as you save" was removed too (QA 2026-10-15 #42).
 *
 * Which sections appear comes from the server (`form.askedSections`, the firm's "Ask for"
 * choices, crossmarc/steward-app#1882): {@link shownSteps}. The client's own details and the
 * address are always shown; an absent field shows every section. The SPA only hides. The
 * server does not refuse a save of a section that was not asked for, since no screen reaches one.
 *
 * What the form shows comes from `mergeOnboardingPrefill` (steward-types): per section, saved
 * answers win, and an unsaved section shows what the snapshot said, with no
 * provenance label (the "From your snapshot" hints were removed, steward-app#2419).
 *
 * Every read and write needs a recent step-up (view; reveal for the SSN). The API client's
 * interceptor runs that ceremony and retries, so this file never handles `STEP_UP_REQUIRED`.
 *
 * The SSN is never held in this form's answers. It is typed into its own box and sent once to
 * the tax-id route, as the mock draws it (no Save button of its own): when the box loses focus
 * holding nine digits, and on Continue or a nav click, which wait for it and stay put if the
 * number is incomplete or refused. From then on it is shown as the last four digits the
 * server returns. Setting it needs the stronger (reveal) check, so that is when the step-up
 * prompt appears.
 *
 * **Deviations from the signed-off mock**, listed for the PR:
 *   - the mock's line under the nav that names an advisor as reading the answers is omitted
 *     (coordinator ruling): no advisor's name is carried to this client, and the welcome page
 *     makes no such promise;
 *   - Employer and Title are two fields, as on intake (#1904; the mock drew one joined field);
 *   - "Saved just now" becomes "Saved at {time}" after a minute, so it stays true;
 *   - children (crossmarc/steward-app#1812) is not in the mock at all, so its panel follows the
 *     existing section and field style rather than a drawn layout: a repeatable
 *     name-and-date-of-birth row (see {@link ChildrenSection}).
 *
 * #1812 also added a CPA and an attorney panel, on the same "not in the mock" reasoning. Matt
 * ruled (2026-09-26, crossmarc/steward-app#1850) that this was a mistake, not a deviation to
 * live with: RIPL's signed-off mocks never drew a CPA or attorney step at all, so #1850 removed
 * both from this form. `ONBOARDING_CLIENT_FORM_SECTIONS` (steward-types) is the section list
 * this file iterates; it excludes `cpa`/`attorney` even though appcore's schema and
 * `ONBOARDING_FORM_SECTIONS` (the wider list) still declare them, for rows #1812 already wrote.
 */

/** When the client last sent this form, or null if they have not (read off the saved answers). */
export function sentAtOf(form: Pick<OnboardingFormView, 'details'>): string | null {
  const v = form.details?.attributes?.[ONBOARDING_SENT_AT_KEY];
  return typeof v === 'string' ? v : null;
}

/** Which of the form's three views is showing: the sections, the "thank you" page after sending, or
 *  the client's requests page (steward-app#2433, `/onboarding/requests`, which the request email
 *  links to). All three are one mounted component, so the client's edits survive moving between them. */
export type OnboardingFormPage = 'form' | 'completed' | 'requests';

type Step = OnboardingClientFormSection | 'review';
/** The sections shown for this invitation, in form order, plus Review (steward-app#1882). Exported
 *  so a rail that numbers its steps can count `shownSteps(form).length` (steward-app#1886). */
export function shownSteps(form: Pick<OnboardingFormView, 'askedSections'>): Step[] {
  return [...clientFormSectionsFor(form.askedSections), 'review'];
}
type SaveState = { kind: 'idle' } | { kind: 'saving' } | { kind: 'saved'; at: number } | { kind: 'error'; message: string };

const AUTOSAVE_MS = 800;
const NOT_READY = 'ONBOARDING_NOT_READY';

/**
 * Worth retrying: a failure that may pass, that is a 5xx, or no reply at all (offline, timeout). Not a 4xx,
 * and not a cancelled step-up: the interceptor rejects a dismissed ceremony with an `Error`
 * subclass, `StepUpCancelledError` ('Step-up verification was cancelled.', api/stepUp.ts), which
 * is neither of those, so it
 * is never retried and the prompt is not shown again a second later. The landing's read and the form's read
 * both use this, so a cancel is not re-prompted by either. A 409 ONBOARDING_NOT_READY is a 4xx
 * and so is not retried here either: the form polls through it (`refetchInterval`) instead.
 */
export { isTransientReadFailure };

const isNotReady = (err: unknown) => err instanceof ApiError && err.code === NOT_READY;

/**
 * Verbatim the façade's `ONBOARDING_FORM_CLOSED_MESSAGE`
 * (`services/steward-app-api/src/handlers/onboardingForm.ts`), so the up-front notice
 * (crossmarc/steward-app#1856 bullet 5) says the same thing a save-time refusal already
 * says (`userMessage` passes that message through unchanged: `CONFLICT` has no
 * `FRIENDLY_ERROR_CODE` entry). Duplicated rather than imported: the façade and the SPA are
 * separate packages, and this string is the whole of the coupling between them.
 */
const FORM_CLOSED_MESSAGE =
  'Your advisor has set up Steward for you, so this form is closed. You can keep your details up to date in Steward.';

/**
 * The closed form's sentence. The firm's own Close form (crossmarc/steward-app#2550) names the
 * firm; a set-up closure, or an older façade that does not say who closed it, keeps
 * {@link FORM_CLOSED_MESSAGE}.
 */
export function closedMessage(by: 'firm' | 'setup' | undefined, firmName: string | null): string {
  if (by !== 'firm') return FORM_CLOSED_MESSAGE;
  const name = firmName?.trim();
  return name ? CLIENT_FORM_CLOSURE_COPY.clientNotice(name) : CLIENT_FORM_CLOSURE_COPY.clientNoticeNoFirm;
}

/** Moved to `onboardingRecord.ts` with the rest of the record conversion; re-exported for the callers and tests that import it from here. */
export { sectionPayload };

/**
 * The answers as this client's form can stand behind them. A spouse the firm did not ask about
 * cannot be answered here, so a snapshot's `spouse_included: true` must not make the address
 * step wait on spouse address fields, nor let the trusted contact be a spouse the form never
 * showed; likewise children the family page never showed cannot be chosen as the trusted contact.
 */
function answersView(v: Values, asked: OnboardingFormView['askedSections']): Values {
  const shown = shownSteps({ askedSections: asked });
  const out: Values = { ...v };
  if (!shown.includes('spouse')) delete out['spouse_included'];
  if (!shown.includes('children')) { delete out['children_names']; delete out['children_dateOfBirths']; }
  return out;
}

/** Nine digits, however the client typed them, or null. The server normalises again. */
export function taxIdDigits(raw: string): string | null {
  const digits = raw.replace(/[\s-]/g, '');
  return /^\d{9}$/.test(digits) ? digits : null;
}

function firstName(values: Values, s: 'client' | 'spouse'): string {
  const v = values[`${s}_firstName`];
  return typeof v === 'string' ? v.trim() : '';
}

function fullName(values: Values, s: 'client' | 'spouse'): string {
  const last = values[`${s}_lastName`];
  return [firstName(values, s), typeof last === 'string' ? last.trim() : ''].filter(Boolean).join(' ');
}

/**
 * Names the first answer the record would not take, and the step it is on when every held-back answer
 * is on that one step, so the client knows where to look. Answers spread across several steps name no step.
 */
export function heldBackMessage(ids: readonly string[]): string {
  const keyOf = (id: string): string | undefined =>
    id === 'spousePartner.children' ? 'children_names' : Object.entries(ONBOARDING_TO_UNIFIED).find(([, u]) => u === id)?.[0];
  const resolve = (id: string): { label: string; page: Page; plural: boolean } | undefined => {
    const key = keyOf(id);
    const section = (Object.keys(ONBOARDING_FORM_FIELDS) as OnboardingFormSection[]).find((sec) => ONBOARDING_FORM_FIELDS[sec].some((f) => f.key === key));
    const spec = section === undefined ? undefined : ONBOARDING_FORM_FIELDS[section].find((f) => f.key === key);
    if (section === undefined || spec === undefined || section === 'cpa' || section === 'attorney') return undefined;
    const page: Page = section === 'spouse' || section === 'children' ? 'family' : section;
    // "Legal address: street" is an internal grouping, and the step has three addresses, so the
    // qualifier becomes words: "the street of your legal address".
    const [group, part] = spec.label.split(': ');
    const plain = part === undefined || group === undefined
      ? `"${spec.label}"`
      : `the ${part} of ${/^spouse /i.test(group) ? `your spouse's ${group.slice(7).toLowerCase()}` : `your ${group.toLowerCase()}`}`;
    return id === 'spousePartner.children' ? { label: 'your children', page, plural: true } : { label: plain, page, plural: false };
  };
  const all = ids.map(resolve);
  const first = all.find((r) => r !== undefined);
  if (first === undefined) return 'Some answers could not be saved. Please check them and try again.';
  const others = ids.length - 1;
  const onOneStep = all.every((r) => r !== undefined && r.page === first.page);
  const head = first.plural ? `We could not save your answers about ${first.label}` : `We could not save your answer for ${first.label}`;
  const where = onOneStep ? ` in "${stepLabel(first.page)}"` : '';
  const alsoNot = others > 0 ? ` We could not save ${others} other ${others === 1 ? 'answer' : 'answers'} either.` : '';
  return `${head}${where}.${alsoNot} Please check ${first.plural || others > 0 ? 'them' : 'it'} and try again.`;
}

/** The rail's and the Review page's label for a page. Static: the first section is "Your
 *  details" on both forms, not the client's own name (crossmarc/steward-app#1886), and the
 *  spouse and the children share "Your family" (#1887). */
function stepLabel(page: Page): string {
  switch (page) {
    case 'client': return 'Your details';
    case 'requests': return 'Requests from your advisor';
    case 'family': return 'Your family';
    case 'address': return 'Home and mailing address';
    case 'trustedContact': return 'Trusted contact';
    case 'beneficiary': return 'Beneficiary wishes';
    case 'about': return 'About you (optional)';
    case 'review': return 'Review and send';
  }
}

/**
 * The form's pages, in order. The stored sections `spouse` and `children` stay two (their save
 * calls and saved flags are unchanged) but share the one page `family`, which exists when either
 * is shown (crossmarc/steward-app#1887).
 */
export function shownPages(form: Pick<OnboardingFormView, 'askedSections' | 'requests'>): Page[] {
  const out: Page[] = [];
  for (const s of shownSteps(form)) {
    if (s === 'spouse' || s === 'children') { if (!out.includes('family')) out.push('family'); } else out.push(s);
    // The advisor's requests (steward-app#2284) come right after the first step, and only when
    // the form carries one: with none, the pages, the counts and the review are exactly as before.
    if (s === 'client' && (form.requests?.length ?? 0) > 0) out.push('requests');
  }
  return out;
}

/** The cap of one form key: the unified question's own, so the box stops where the record's validator does. */
export function capOfKey(key: string, fallback: number): number {
  const id = ONBOARDING_TO_UNIFIED[key];
  return (id === undefined ? undefined : unifiedQuestionById(id)?.maxLength) ?? fallback;
}

/** "180 of 200 characters" from 90 percent of the cap (founder decision D6), read with the field. */
function Counter({ id, length, max }: { id: string; length: number; max: number }) {
  const text = counterText(length, max);
  return text === null ? null : <p id={id} className="mt-1 mb-0 text-right text-[12px] text-slate font-sans">{text}</p>;
}

export function Field({ spec, value, onChange, mark }: {
  spec: OnboardingFieldSpec; value: string | boolean | undefined; onChange: (v: string | boolean) => void;
  /** "Entered by your advisor, {date}" while the advisor's answer stands (#2538). */
  mark?: string | undefined;
}) {
  const id = `onb-${spec.key}`;
  const text = typeof value === 'string' ? value : '';
  const long = spec.kind === 'longText';
  const max = capOfKey(spec.key, long ? ONBOARDING_STRING_MAX : 200);
  const counterId = counterText(text.length, max) === null ? undefined : `${id}-counter`;
  if (long) {
    return (
      <div className={formCls.field}>
        <label htmlFor={id} className={formCls.label}>{spec.label}</label>
        <textarea id={id} className={formCls.textarea} rows={3} maxLength={max} aria-describedby={counterId}
          value={text} onChange={(e) => onChange(e.target.value)} />
        <Counter id={`${id}-counter`} length={text.length} max={max} />
        {mark && <p data-testid="client-staff-mark" className="mt-1 mb-0 text-[12px] text-slate font-sans">{mark}</p>}
      </div>
    );
  }
  return (
    <div className={formCls.field}>
      <label htmlFor={id} className={formCls.label}>{spec.label}</label>
      <input id={id} className={formCls.input} type={spec.kind === 'date' ? 'date' : 'text'} maxLength={max}
        aria-describedby={counterId} autoComplete="off" value={text} onChange={(e) => onChange(e.target.value)} />
      <Counter id={`${id}-counter`} length={text.length} max={max} />
      {mark && <p data-testid="client-staff-mark" className="mt-1 mb-0 text-[12px] text-slate font-sans">{mark}</p>}
    </div>
  );
}

export function YesNo({ label, value, onChange, name }: { label: string; value: boolean | undefined; onChange: (v: boolean) => void; name: string }) {
  return (
    <fieldset className="border-none p-0 m-0 mb-4">
      <legend className={formCls.label}>{label}</legend>
      <div className="flex gap-4 text-[13px] text-navy font-sans">
        <label className="flex items-center gap-1.5 cursor-pointer">
          <input type="radio" name={name} checked={value === true} onChange={() => onChange(true)} /> Yes
        </label>
        <label className="flex items-center gap-1.5 cursor-pointer">
          <input type="radio" name={name} checked={value === false} onChange={() => onChange(false)} /> No
        </label>
      </div>
    </fieldset>
  );
}

/**
 * Children: a name and a date of birth per child (crossmarc/steward-app#1812), matching the
 * snapshot's `spousePartner.children` shape. Storage is two index-aligned lists,
 * `children_names` and `children_dateOfBirths` (child N's name is `names[N]`, their date of
 * birth `dateOfBirths[N]`), not a list of objects: appcore's pack schema has no field type for
 * that, and every other section here is already a flat set of keys. This component is the only
 * place the two lists are zipped into rows for editing and split back for saving. Removing a
 * row removes that index from BOTH lists, so the two stay the same length; a row added but
 * left entirely blank is dropped by {@link sectionPayload} like any other blank answer.
 */
/**
 * A client-side row cap, well under appcore's actual limit (`ONBOARDING_ARRAY_MAX_ITEMS` = 50,
 * `packages/core/src/db/onboarding-form-repository.ts`) so nobody hits that as a save-time 400
 * with no row-specific message; 20 comfortably covers any real family and leaves headroom.
 */
const MAX_CHILDREN = 20;

// steward-app#2513: the Social Security number row's small buttons (Change, Remove, Show, Hide, Cancel)
// are 44px tall below `sm`; `btnSm` itself is shared with dense desktop toolbars and stays as it was.
const SSN_BTN = `${btns.btnSm} max-sm:min-h-11`;

/**
 * The Show/Hide control beside a Social Security number box, in the client's form and in staff
 * entry (steward-app#2770). A real button whose accessible name IS its visible text ("Show number",
 * then "Hide number"), so a screen reader and a voice-control user say what they see (WCAG 2.5.3).
 * No `aria-pressed` on top: a changing label already says the state, and the two together
 * would announce it twice.
 */
export function SsnToggle({ shown, onToggle, className = SSN_BTN }: { shown: boolean; onToggle: () => void; className?: string }) {
  return (
    <button type="button" className={className} onClick={onToggle}>
      {shown ? 'Hide number' : 'Show number'}
    </button>
  );
}

export function ChildrenSection({ names, dateOfBirths, onChange }: {
  names: string[];
  dateOfBirths: string[];
  onChange: (names: string[], dateOfBirths: string[]) => void;
}) {
  const rowCount = Math.max(names.length, dateOfBirths.length);
  const childNameCap = capOfKey('children_names', 200);
  const rows = Array.from({ length: rowCount }, (_, i) => ({ name: names[i] ?? '', dateOfBirth: dateOfBirths[i] ?? '' }));

  function setRow(i: number, field: 'name' | 'dateOfBirth', v: string) {
    const nextNames = names.slice(0, rowCount);
    const nextDobs = dateOfBirths.slice(0, rowCount);
    while (nextNames.length <= i) nextNames.push('');
    while (nextDobs.length <= i) nextDobs.push('');
    if (field === 'name') nextNames[i] = v; else nextDobs[i] = v;
    onChange(nextNames, nextDobs);
  }

  return (
    <div>
      {rows.length === 0 && <p className="text-[13px] text-mid font-sans mt-0 mb-4">No children added yet.</p>}
      {rows.map((row, i) => (
        <fieldset key={i} className="border border-line rounded-token p-3 mb-3">
          <legend className="text-[11px] uppercase tracking-[0.08em] text-slate font-sans px-1">Child {i + 1}</legend>
          <div className={formCls.field}>
            <label htmlFor={`onb-children-name-${i}`} className={formCls.label}>Name</label>
            <input id={`onb-children-name-${i}`} className={formCls.input} type="text" maxLength={childNameCap} autoComplete="off"
              aria-describedby={counterText(row.name.length, childNameCap) === null ? undefined : `onb-children-name-${i}-counter`}
              value={row.name} onChange={(e) => setRow(i, 'name', e.target.value)} />
            <Counter id={`onb-children-name-${i}-counter`} length={row.name.length} max={childNameCap} />
          </div>
          <div className={formCls.field}>
            <label htmlFor={`onb-children-dob-${i}`} className={formCls.label}>Date of birth</label>
            <input id={`onb-children-dob-${i}`} className={formCls.input} type="date"
              value={row.dateOfBirth} onChange={(e) => setRow(i, 'dateOfBirth', e.target.value)} />
          </div>
          <button type="button" className={btns.btnSm} onClick={() => onChange(
            names.filter((_, idx) => idx !== i), dateOfBirths.filter((_, idx) => idx !== i),
          )}>Remove</button>
        </fieldset>
      ))}
      {rowCount < MAX_CHILDREN
        ? <button type="button" className={btns.btn} onClick={() => onChange([...names, ''], [...dateOfBirths, ''])}>+ Add a child</button>
        : <p className="text-[13px] text-mid font-sans m-0">
            You have reached the limit of {MAX_CHILDREN} children. If you need to list more, let your advisor know.
          </p>}
    </div>
  );
}

/** Hides typed digits without making the box a password field (see `TaxIdField`). */
export const MASKED = '[-webkit-text-security:disc] [text-security:disc]';

/**
 * Whether this browser masks with {@link MASKED}. `-webkit-text-security` is non-standard.
 * Per MDN browser-compat-data (`css/properties/-webkit-text-security.json`, main branch, read
 * 2026-09-25): Chrome 1, Safari 3, Safari iOS 1, Firefox 114 (June 2023), and Edge, Opera,
 * Samsung and the WebViews mirror Chromium or Safari. Our build targets `es2022`, which Firefox
 * 93 to 113 can run WITHOUT the property, so where it is unsupported the hidden box falls back
 * to `type="password"`: the digits must never render in the clear, and a password manager's
 * offer is the lesser harm there.
 */
export function textSecuritySupported(): boolean {
  try {
    return typeof CSS !== 'undefined' && typeof CSS.supports === 'function'
      && CSS.supports('-webkit-text-security', 'disc');
  } catch { return false; }
}

/** Saves a typed SSN: `true` when there is nothing left unsaved, `false` when it stays put. */
export type TaxIdFlush = () => Promise<boolean>;

/**
 * The SSN box: typed, shown or hidden. There is no Save button (the mock has none). It saves
 * when it loses focus holding nine digits, and when the form asks it to through
 * `registerFlush` (Continue, Back or a nav click). Once saved, only the last four digits the
 * server returns are ever on screen, and the typed digits are cleared. The note under it is
 * the mock's copy, verbatim.
 */
export function TaxIdField({ subject, firm, firmName, identity, onSave, onClear, registerFlush }: {
  subject: 'client' | 'spouse';
  firm: ReactNode;
  /** For the change note's wording (docs/43 §8.4). */
  firmName?: string | null;
  identity: OnboardingIdentityView | undefined;
  onSave: (digits: string) => Promise<void>;
  onClear: () => Promise<void>;
  registerFlush?: (subject: 'client' | 'spouse', flush: TaxIdFlush | null) => void;
}) {
  const [raw, setRaw] = useState('');
  const [show, setShow] = useState(false);
  const [editing, setEditing] = useState(!identity);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const rawRef = useRef(raw);
  rawRef.current = raw;
  const inflight = useRef<Promise<boolean> | null>(null);
  const [cssMask] = useState(textSecuritySupported);
  const id = `onb-${subject}-ssn`;

  /** `quiet`: a blur with an incomplete number says nothing yet; Continue does. */
  const save = useCallback((quiet: boolean): Promise<boolean> => {
    if (inflight.current) return inflight.current;
    const typed = rawRef.current;
    if (typed.trim() === '') return Promise.resolve(true);
    const digits = taxIdDigits(typed);
    if (!digits) {
      if (!quiet) setError('Enter all nine digits.');
      return Promise.resolve(false);
    }
    setBusy(true); setError(null);
    inflight.current = onSave(digits).then(
      () => { setRaw(''); rawRef.current = ''; setShow(false); setEditing(false); return true; },
      (err: unknown) => { setError(userMessage(err, 'That number could not be saved. Please try again.')); return false; },
    ).finally(() => { setBusy(false); inflight.current = null; });
    return inflight.current;
  }, [onSave]);

  // Typed digits do not outlive the tab being hidden (review F2): a saved number is already on
  // the server, and an unsaved one is retyped rather than left on screen.
  useEffect(() => {
    const onVis = () => {
      if (document.visibilityState === 'hidden' && !inflight.current) { setRaw(''); rawRef.current = ''; setShow(false); setError(null); }
    };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, []);

  useEffect(() => {
    if (!registerFlush) return;
    registerFlush(subject, () => save(false));
    return () => registerFlush(subject, null);
  }, [registerFlush, subject, save]);

  async function clear() {
    setBusy(true); setError(null);
    try {
      await onClear();
      setEditing(true);
    } catch (err) {
      setError(userMessage(err, 'That number could not be removed. Please try again.'));
    } finally { setBusy(false); }
  }

  return (
    <div className={formCls.field} data-testid={`onboarding-ssn-${subject}`}>
      <label htmlFor={id} className={formCls.label}>Social Security number</label>
      {identity && !editing ? (
        <div className="flex flex-wrap items-center gap-3">
          <span className="font-mono text-[13px] text-navy" data-testid={`onboarding-ssn-${subject}-masked`}>
            •••-••-<UserText>{identity.taxIdHint}</UserText>
          </span>
          <button type="button" className={SSN_BTN} disabled={busy} onClick={() => { setEditing(true); setError(null); }}>Change</button>
          <button type="button" className={SSN_BTN} disabled={busy} onClick={() => void clear()}>Remove</button>
        </div>
      ) : (
        <div className="flex flex-wrap items-center gap-2">
          {/* type=text masked by CSS, not type=password: a password manager offers to save a
              password field (review F2), and this is not a credential. Where the CSS mask is
              unsupported, hidden falls back to type=password (`textSecuritySupported`). */}
          <input id={id} className={`${formCls.input} max-w-[220px] ${show ? '' : MASKED}`}
            type={show || cssMask ? 'text' : 'password'} inputMode="numeric"
            autoComplete="off" data-1p-ignore data-lpignore="true" data-form-type="other" spellCheck={false}
            value={raw} disabled={busy}
            onChange={(e) => { setRaw(e.target.value.replace(/[^\d\s-]/g, '').replace(/^\s+/, '').slice(0, 11)); if (error) setError(null); }}
            onBlur={() => {
              // Switching tabs or windows blurs the box BEFORE `visibilitychange` fires, so
              // `visibilityState` usually still reads 'visible' here. Saving then would leave a
              // save in flight when the page goes hidden, and the in-flight save would stop the
              // hidden handler clearing the digits (review S1). So a blur saves only when the
              // document still has focus: `hasFocus()` is false when the window itself lost
              // focus, and true for an in-page move such as clicking Continue (which saves via
              // `registerFlush` anyway). The `visibilityState` check stays for a browser that
              // flips it first. That event ordering is browser behaviour and is NOT verified in
              // CI; the tests stub both values. A skipped blur keeps the digits only until the
              // hidden handler clears them, and the family retypes them when they come back.
              if (document.visibilityState === 'hidden' || !document.hasFocus()) return;
              if (rawRef.current.trim() !== '' && !taxIdDigits(rawRef.current)) setError('Enter all nine digits.');
              void save(true);
            }} />
          <SsnToggle shown={show} onToggle={() => setShow((s) => !s)} />
          {identity && <button type="button" className={SSN_BTN} disabled={busy} onClick={() => { setEditing(false); setRaw(''); setError(null); }}>Cancel</button>}
          {busy && <span className="text-[12px] text-slate font-sans">Saving...</span>}
        </div>
      )}
      <p className="flex items-start gap-1.5 text-[11px] leading-relaxed text-slate font-sans m-0 mt-2">
        <Lock size={12} strokeWidth={1.75} aria-hidden className="mt-0.5 shrink-0" />
        <span>
          Stored encrypted. After you save it, it shows only the last four digits. {firm} can view the full number to open your
          accounts.
        </span>
      </p>
      {/* docs/43 §8.4 (slice 9). One line, never both: Matt's note when the advisor's number
          replaced the client's own, else the plain mark on an advisor-entered one. Both date from
          `updatedAt`, the latest write: `replacedClientValueAt` stamps only the FIRST replacement,
          so a later staff write would otherwise be dated by an older one (decided 2026-11-02). */}
      {identity && !editing && (typeof identity.replacedClientValueAt === 'string' ? (
        <p role="note" className="text-[12px] text-navy font-sans m-0 mt-2" data-testid={`onboarding-ssn-${subject}-replaced`}>
          {clientSsnReplacedNote(firmName, markDate(identity.updatedAt))}
        </p>
      ) : identity.enteredBy === 'advisor' && (
        <p className="text-[11px] text-slate font-sans m-0 mt-1" data-testid={`onboarding-ssn-${subject}-staff-mark`}>
          {clientStaffMark(markDate(identity.updatedAt))}
        </p>
      ))}
      {error && <p className={formCls.error} role="alert">{error}</p>}
    </div>
  );
}

/** Loads the form, then hands it to {@link OnboardingFormBody}. */
export function OnboardingForm({ token, firmWorkspaceId, firmName, onSignOut, view, onViewChange }: {
  token: string;
  firmWorkspaceId: string;
  firmName: string | null;
  onSignOut?: (outcome?: { sent: boolean; requests?: boolean }) => void;
  /** See {@link OnboardingFormBody}. */
  view?: OnboardingFormPage;
  onViewChange?: (view: OnboardingFormPage) => void;
}) {
  const q = useQuery({
    queryKey: onboardingFormQueryKey(firmWorkspaceId),
    queryFn: () => getOnboardingForm(token, firmWorkspaceId),
    retry: (count, err) => isTransientReadFailure(err) && count < 2,
    // "Still being prepared" is a moment's wait for the intake writer; poll through it.
    refetchInterval: (query) => (query.state.data === undefined && isNotReady(query.state.error) ? 4000 : false),
    staleTime: Infinity,
  });

  // The answers live on the client record (docs/43 slice 4c). It is read once the form says which
  // firm this is and that it is still open, and it is read fresh on every mount: the page's edits live
  // in the body's state, so a cached copy from an earlier visit would be an older base than the
  // record now holds.
  const firmSlug = q.data?.firmSlug;
  const recordEnabled = !!q.data && !q.data.formClosed && !!firmSlug;
  const rq = useQuery(onboardingRecordQuery(token, firmSlug, recordEnabled));

  const shell = (children: ReactNode, centered = false) => (
    <div className={`flex min-h-screen flex-col items-center bg-cream px-4 pb-12 ${centered ? 'justify-center' : 'pt-[8vh]'}`}>
      <div className="w-full max-w-[420px] bg-surface border border-line shadow-panel rounded-token px-6 py-8 flex flex-col gap-4">{children}</div>
      <LeaveControl onSignOut={onSignOut && (() => onSignOut())} className="mt-6" />
    </div>
  );

  // Centred on the page, as the gate's own loader is (steward-app#2470); the other states stay top-aligned.
  if (q.isLoading) return shell(<p className="m-0 text-[13px] text-mid font-sans">Loading your details...</p>, true);
  // The not-ready and load-error shells are for a form that has never loaded. A later read that
  // fails (the requests step re-reads the form after a lost upload answer) keeps `data` in the
  // query, and the client keeps the form they are filling in.
  if (!q.data && q.isError && isNotReady(q.error)) {
    return shell(
      <div data-testid="onboarding-form-not-ready">
        <h1 className="m-0 font-serif text-[20px] font-normal text-navy">Getting your form ready</h1>
        <p className="m-0 mt-2 text-[13px] text-mid font-sans">This usually takes a moment. It will open by itself when it is ready.</p>
        <button type="button" className={`${btns.btn} mt-4`} onClick={() => void q.refetch()}>Try again</button>
      </div>,
    );
  }
  if (!q.data) {
    return shell(
      <div role="alert">
        <p className="m-0 text-[13px] text-mid font-sans">{userMessage(q.error, 'Your form could not be loaded. Please try again.')}</p>
        <button type="button" className={`${btns.btn} mt-4`} onClick={() => void q.refetch()}>Try again</button>
      </div>,
    );
  }
  // crossmarc/steward-app#1856 bullet 5: shown as soon as the form loads, not only once a
  // save is refused. No editable fields and no save button: this replaces the form entirely
  // rather than disabling it in place, so there is nothing here for the client to try again.
  if (q.data.formClosed) {
    return shell(
      <div data-testid="onboarding-form-closed">
        <h1 className="m-0 font-serif text-[20px] font-normal text-navy">Your form is closed</h1>
        <p className="m-0 mt-2 text-[13px] text-mid font-sans">{closedMessage(q.data.formClosedBy, firmDisplayName(q.data.brandKey, q.data.firmName || firmName))}</p>
      </div>,
    );
  }
  if (!firmSlug) {
    return shell(
      <div role="alert">
        <p className="m-0 text-[13px] text-mid font-sans">Your form could not be loaded. Please try again.</p>
        <button type="button" className={`${btns.btn} mt-4`} onClick={() => void q.refetch()}>Try again</button>
      </div>,
    );
  }
  // A copy another observer left in the cache is not shown: the body takes its answers from the record once, so the first read after this mount decides.
  if (rq.isLoading || (recordEnabled && !rq.isFetchedAfterMount && !rq.isError)) return shell(<p className="m-0 text-[13px] text-mid font-sans">Loading your details...</p>, true);
  if (!rq.data && rq.isError && isRecordNotReady(rq.error)) {
    return shell(
      <div data-testid="onboarding-form-not-ready">
        <h1 className="m-0 font-serif text-[20px] font-normal text-navy">Getting your form ready</h1>
        <p className="m-0 mt-2 text-[13px] text-mid font-sans">This usually takes a moment. It will open by itself when it is ready.</p>
        <button type="button" className={`${btns.btn} mt-4`} onClick={() => void rq.refetch()}>Try again</button>
      </div>,
    );
  }
  if (!rq.data) {
    return shell(
      <div role="alert">
        <p className="m-0 text-[13px] text-mid font-sans">{userMessage(rq.error, 'Your form could not be loaded. Please try again.')}</p>
        <button type="button" className={`${btns.btn} mt-4`} onClick={() => void rq.refetch()}>Try again</button>
      </div>,
    );
  }
  return <OnboardingFormBody token={token} firmWorkspaceId={firmWorkspaceId} firmName={q.data.firmName || firmName} form={q.data} firmSlug={firmSlug} record={rq.data} onSignOut={onSignOut} view={view} onViewChange={onViewChange} />;
}

/** The information-request answers, which stay on the legacy row; every other value comes from the record. */
function requestValuesOf(source: Values): Values {
  const out: Values = {};
  for (const { key } of ONBOARDING_FORM_FIELDS.requests) if (source[key] !== undefined) out[key] = source[key];
  return out;
}

/** Where the client was, kept in this browser per firm (not a secret, and losing it costs one click). */
const resumeKey = (firmWorkspaceId: string) => `steward.onboarding.step.${firmWorkspaceId}`;
function readResume(firmWorkspaceId: string, steps: readonly Page[]): Page | null {
  try {
    const v = window.localStorage.getItem(resumeKey(firmWorkspaceId));
    return v !== null && (steps as readonly string[]).includes(v) ? (v as Page) : null;
  } catch { return null; }
}
function writeResume(firmWorkspaceId: string, step: Page): void {
  try { window.localStorage.setItem(resumeKey(firmWorkspaceId), step); } catch { /* storage may be off */ }
}

/**
 * `view` and `onViewChange` let the gate keep the completed page in the URL (`/onboarding/done`)
 * while this component stays MOUNTED across "Review my answers" and the send: the form's edits
 * live in this component's state, not in the query cache, so a remount would reload older answers.
 * Left out (a test, a harness), the view is plain local state starting on the form.
 * The completed page only ever shows once the form has been sent: asking for it before that
 * (a deep link, a reload) shows the form, so there is never a thank-you for nothing sent.
 */
export function OnboardingFormBody({ token, firmWorkspaceId, firmName, form, firmSlug, record, onSignOut, view: viewProp, onViewChange }: {
  token: string;
  firmWorkspaceId: string;
  firmName: string | null;
  form: OnboardingFormView;
  /** The firm's slug, which addresses the record's routes. */
  firmSlug: string;
  /** The client's record as it was read when the form opened: the answers the form starts on. */
  record: OnboardingRecordView;
  /** `sent` is whether the client has sent the form, for the signed-out card (#2432). */
  onSignOut?: (outcome?: { sent: boolean; requests?: boolean }) => void;
  view?: OnboardingFormPage;
  onViewChange?: (view: OnboardingFormPage) => void;
}) {
  const backToApp = useContext(OnboardingBackToAppContext);
  const queryClient = useQueryClient();
  // The version and the copy the page is built on. They move when a save lands, and when the page
  // takes a merged copy; never on a read the page did not adopt (see `onboardingRecord.ts`).
  const base = useRef<RecordBase | null>(null);
  base.current ??= baseOf(record);
  const [initial] = useState(() => ({
    // The record's answers, plus the information-request answers, which stay on the legacy row.
    values: { ...requestValuesOf(mergeOnboardingPrefill(form.details?.attributes, null).values as Values), ...wireToValues(baseOf(record).stored) } as Values,
    fromPrefill: filledInKeys(record),
  }));
  const [values, setValues] = useState<Values>(initial.values);
  const [fromPrefill, setFromPrefill] = useState<Set<string>>(initial.fromPrefill);
  // The answers the client's advisor entered (docs/43 §4, #2538). A key leaves as soon as the
  // client changes it: the save gives it the client's provenance.
  const [staffAt, setStaffAt] = useState<Map<string, string>>(() => staffKeysOf(record));
  const markFor = (key: string): string | undefined => {
    const at = staffAt.get(key);
    return at === undefined ? undefined : clientStaffMark(markDate(at));
  };
  const [identities, setIdentities] = useState<OnboardingIdentityView[]>(form.identities);
  const [sentAt, setSentAt] = useState<string | null>(record.onboardingSentAt ?? sentAtOf(form));
  const [localView, setLocalView] = useState<OnboardingFormPage>('form');
  const view = viewProp ?? localView;
  const setView = (v: OnboardingFormPage) => { setLocalView(v); onViewChange?.(v); };
  const showingCompleted = view === 'completed' && !!sentAt;
  const showingRequests = view === 'requests';
  // True from a send that lands on the requests page until the client leaves it (steward-app#2471),
  // so the "Your answers are with" line shows on arrival from send() and never from the email link.
  const [justSent, setJustSent] = useState(false);
  useEffect(() => { if (!showingRequests) setJustSent(false); }, [showingRequests]);
  // The firm as the client's page names it (the brand's short name where there is one), for the
  // header and the notice alike: one firm is never named two ways (steward-app#2770).
  const displayName = firmDisplayName(form.brandKey, firmName);
  const steps = shownPages(form);
  const shownSections = shownSteps(form).filter((x): x is OnboardingClientFormSection => x !== 'review');
  const pageSections = steps.filter((x): x is AnswerPage => x !== 'review');
  const requests = form.requests ?? [];
  const requestUploads = useInfoRequestUploads(token, firmWorkspaceId, requests);
  // The server's word on which account numbers are saved (docs/38 §5); the card updates the cached read it comes from.
  const filledNumbers = filledNumberIds(form.accountNumbers);
  const [step, setStepState] = useState<Page>(() => readResume(firmWorkspaceId, steps) ?? 'client');
  // The page the client's notice about advisor-entered answers shows on: the first one drawn in
  // this visit while any such answer stands (the record is in hand at mount), so a client who
  // resumes mid-form still sees it, once (steward-app#2770). A page that holds an advisor-entered
  // answer shows it too.
  const noticePage = useRef<Page | null>(null);
  if (noticePage.current === null && staffAt.size > 0) noticePage.current = step;
  const pageHoldsStaffMark = (p: Page): boolean => {
    const sectionsOfPage: string[] = p === 'family' ? ['spouse', 'children'] : [p];
    const fields = ONBOARDING_FORM_FIELDS as unknown as Record<string, readonly { key: string }[] | undefined>;
    return sectionsOfPage.some((s) => fields[s]?.some((f) => staffAt.has(f.key)) ?? false);
  };
  const [visited, setVisited] = useState<ReadonlySet<Page>>(() => new Set());
  const setStep = (next: Page) => { setStepState(next); setVisited((prev) => new Set(prev).add(next)); writeResume(firmWorkspaceId, next); };
  // A re-read can take the step away (the last request was withdrawn while the client was on it).
  // Move to the nearest step that is still there, preferring the one after, rather than leave the
  // page on a step the form no longer has. Set during render so no frame shows "Section 0 of N".
  const stepsSeen = useRef(steps);
  if (!steps.includes(step)) {
    const was = stepsSeen.current;
    const at = was.indexOf(step);
    let next: Page | undefined;
    for (let d = 1; d < was.length && next === undefined; d += 1) {
      next = [was[at + d], was[at - d]].find((c): c is Page => c !== undefined && steps.includes(c));
    }
    setStepState(next ?? steps[0] ?? 'client');
  }
  useEffect(() => { stepsSeen.current = steps; });
  // Names this screen for the tester feedback tab (crossmarc/steward-app#2531, TEMPORARY).
  useTesterFeedbackScreen(
    showingCompleted ? 'Onboarding: sent'
      : showingRequests ? 'Onboarding: requests from your advisor'
      : `Onboarding form: ${stepLabel(step)}`,
  );
  const [requestClosed, setRequestClosed] = useState(false);
  // How many times a card has said its request is gone. `goTo` compares it before and after its
  // flushes, so a closure raised BY a flush is not wiped by the navigation that flush belongs to.
  const closedRaised = useRef(0);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const firstStep = useRef(true);
  // Move focus to the new section's heading after Continue, Back or a rail pick (as the intake does); not on load.
  useEffect(() => {
    if (firstStep.current) { firstStep.current = false; return; }
    headingRef.current?.focus();
  }, [step]);
  // "Review my answers" unmounts the button that was clicked, which would drop focus to the page
  // body: put it on the form's heading instead. (Going the other way, the completed page focuses
  // its own heading on mount.) Not on first render, when the form is simply the page.
  const wasCompleted = useRef(false);
  useEffect(() => {
    if (wasCompleted.current && !showingCompleted && !showingRequests) headingRef.current?.focus();
    wasCompleted.current = showingCompleted;
  }, [showingCompleted, showingRequests]);
  const [saveState, setSaveState] = useState<SaveState>({ kind: 'idle' });
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  /** After a SENT form's "Save my changes" landed. Cleared by the next edit. */
  const [changesSaved, setChangesSaved] = useState(false);

  const valuesRef = useRef(values);
  valuesRef.current = values;
  const dirty = useRef(new Set<OnboardingFormSection>());
  const sendingRef = useRef(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const chain = useRef<Promise<void>>(Promise.resolve());
  const numberFlushes = useRef(new Map<string, NumberFlush>());
  const registerNumberFlush = useCallback((requestId: string, f: NumberFlush | null) => {
    if (f) numberFlushes.current.set(requestId, f); else numberFlushes.current.delete(requestId);
  }, []);
  const taxIdFlushes = useRef(new Map<'client' | 'spouse', TaxIdFlush>());
  const registerTaxIdFlush = useCallback((subject: 'client' | 'spouse', f: TaxIdFlush | null) => {
    if (f) taxIdFlushes.current.set(subject, f); else taxIdFlushes.current.delete(subject);
  }, []);

  const firm: ReactNode = displayName ? <UserText>{displayName}</UserText> : 'Your advisor';

  const viewOf = (v: Values): Values => answersView(v, form.askedSections);

  /**
   * Save what is dirty, one save at a time. The answers go to the record as ONE whole-form save
   * (appcore replaces every question placed on the form, so a save carries them all); the
   * information-request answers still go to their own legacy section. Each failure puts its
   * sections back in `dirty`, so Retry, the next move and Send all see them as unsaved.
   */
  const flush = useCallback((): Promise<void> => {
    if (timer.current) { clearTimeout(timer.current); timer.current = null; }
    chain.current = chain.current.then(async () => {
      const sections = [...dirty.current];
      dirty.current.clear();
      if (sections.length === 0) return;
      const recordSections = sections.filter((x) => x !== 'requests');
      let failure: string | null = null;
      setSaveState({ kind: 'saving' });
      if (recordSections.length > 0) {
        try {
          const sent = valuesRef.current;
          const { wire, heldBack } = valuesToWire(answersView(sent, form.askedSections), base.current!.stored, clientFormSectionsFor(form.askedSections));
          const result = await saveRecord(token, firmSlug, wire, base.current!);
          if (result.kind === 'saved') {
            base.current = result.base;
          } else if (valuesRef.current === sent) {
            // The record had moved. The merge keeps this client's edits over the newer copy, and the
            // page takes it only if nothing was typed meanwhile; otherwise the base stays put and the
            // next save merges again against the copy the page really has.
            base.current = result.base;
            setValues({ ...requestValuesOf(sent), ...wireToValues(result.merged) });
          }
          // Something the record would not take was kept at its stored value: the rest saved, but
          // the page must not be told it all did.
          if (heldBack.length > 0) {
            for (const x of recordSections) dirty.current.add(x);
            failure = heldBackMessage(heldBack);
          }
        } catch (err) {
          for (const x of recordSections) dirty.current.add(x);
          failure = userMessage(err, 'Please check your connection.');
        }
      }
      if (sections.includes('requests')) {
        try {
          await saveOnboardingSection(token, firmWorkspaceId, 'requests', sectionPayload('requests', valuesRef.current));
        } catch (err) {
          dirty.current.add('requests');
          failure ??= userMessage(err, 'Please check your connection.');
        }
      }
      setSaveState(failure === null ? { kind: 'saved', at: Date.now() } : { kind: 'error', message: failure });
    });
    return chain.current;
  }, [token, firmWorkspaceId, firmSlug, form.askedSections]);

  // Save what is pending when the page goes away (best effort; the request may not finish).
  useEffect(() => () => { if (dirty.current.size) void flush(); }, [flush]);

  function setValue(key: string, section: OnboardingFormSection, v: string | boolean | string[]) {
    setValues((prev) => ({ ...prev, [key]: v }));
    setFromPrefill((prev) => { if (!prev.has(key)) return prev; const next = new Set(prev); next.delete(key); return next; });
    setStaffAt((prev) => { if (!prev.has(key)) return prev; const next = new Map(prev); next.delete(key); if (key === 'children_dateOfBirths') next.delete('children_names'); return next; });
    dirty.current.add(section);
    setChangesSaved(false);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => { void flush(); }, AUTOSAVE_MS);
  }

  /**
   * Save the section, and any SSN or account number typed on this step; stay put if one cannot be
   * saved. An account-number card whose request turns out to be gone (a 404 that a re-read confirmed)
   * answers "nothing left unsaved", so the move goes ahead, and the note it raised stays up on the step
   * the client lands on until their NEXT move (`closedRaised`).
   */
  async function goTo(next: Page) {
    const closedBefore = closedRaised.current;
    void flush();
    // A typed, unsaved account number is saved too (docs/38 §5); if it cannot be, the client stays.
    for (const f of [...taxIdFlushes.current.values(), ...numberFlushes.current.values()]) {
      if (!(await f())) return;
    }
    // A note raised while this move was flushing is for the step the client lands on; any older one goes.
    if (closedRaised.current === closedBefore) setRequestClosed(false);
    setStep(next);
    window.scrollTo?.({ top: 0 });
  }

  const identityFor = (s: 'client' | 'spouse') => identities.find((i) => i.subject === s);
  const answersForStatus: Record<string, unknown> = {
    ...viewOf(values),
    identityProvidedClient: identityFor('client') ? true : undefined,
    identityProvidedSpouse: identityFor('spouse') ? true : undefined,
  };
  const status = (s: OnboardingClientFormSection) => onboardingSectionStatus(s, answersForStatus);
  /** False when the change could not be kept because the answers are at their cap (the step says so).
   *  Withdrawn requests the client is still shown count as known, so making room at the cap does not
   *  drop an answer the screen says the firm still has (steward-app#2302). */
  const setAnswer = (requestId: string, text: string, clean?: boolean): boolean => {
    const next = withAnswer(valuesRef.current, requestId, clean ? cleanInfoAnswer(text) : text, [...requests, ...(form.withdrawnRequests ?? [])].map((r) => r.id));
    if (!next) return false;
    setValue('requests_answerIds', 'requests', next.ids);
    setValue('requests_answerTexts', 'requests', next.texts);
    return true;
  };

  async function saveTaxId(subject: 'client' | 'spouse', digits: string) {
    const identity = await setOnboardingTaxId(token, firmWorkspaceId, subject, digits);
    setIdentities((prev) => [...prev.filter((i) => i.subject !== subject), identity]);
    void queryClient.invalidateQueries({ queryKey: onboardingFormQueryKey(firmWorkspaceId), refetchType: 'none' });
  }
  async function clearTaxId(subject: 'client' | 'spouse') {
    await clearOnboardingTaxId(token, firmWorkspaceId, subject);
    setIdentities((prev) => prev.filter((i) => i.subject !== subject));
  }

  /** The requests page's "Done for now": wait for a typed answer or account number that is still
   *  unsaved. False means one could not be saved, so the page stays and says so. */
  async function saveEverything(): Promise<boolean> {
    await flush();
    if (dirty.current.size) return false;
    for (const f of numberFlushes.current.values()) {
      if (!(await f())) return false;
    }
    return true;
  }

  /**
   * A SENT form's primary action (docs/43): appcore refuses a second send (409 CLIENT_FORM_ALREADY_SENT) but takes answer
   * saves, and the firm reads the answers live, so there is nothing to send again. This only saves, and says so only
   * once the save has landed. It makes no send call.
   */
  async function saveChanges() {
    if (sendingRef.current) return;
    sendingRef.current = true;
    setSending(true); setSendError(null); setChangesSaved(false);
    try {
      await flush();
      if (dirty.current.size) throw new Error('Some answers are not saved yet. Please try again.');
      setChangesSaved(true);
    } catch (err) {
      setSendError(userMessage(err, 'Those changes could not be saved. Please try again.'));
    } finally { sendingRef.current = false; setSending(false); }
  }

  async function send() {
    // Once: a second press while one is in flight is nothing (the button is disabled, but a
    // double click can land before the disabled state paints).
    if (sendingRef.current) return;
    sendingRef.current = true;
    setSending(true); setSendError(null);
    try {
      await flush();
      if (dirty.current.size) throw new Error('Some answers are not saved yet. Please try again.');
      const at = await sendOnce();
      setSentAt(at);
      // Open requests: straight to the requests page, with a line saying the form arrived.
      // Nothing outstanding: the thank-you card, as before.
      if (splitRequests(requests, requestUploads.uploads, values, filledNumbers).open.length > 0) {
        setJustSent(true);
        setView('requests');
      } else {
        setView('completed');
      }
    } catch (err) {
      setSendError(userMessage(err, 'That could not be sent. Please try again.'));
    } finally { sendingRef.current = false; setSending(false); }
  }

  /** Sends the form; a form that was already sent is a success, stamped with when it first was. */
  async function sendOnce(): Promise<string> {
    try {
      return (await sendOnboardingRecord(token, firmSlug)).sentAt;
    } catch (err) {
      if (!isAlreadySent(err)) throw err;
      const read = await getOnboardingRecord(token, firmSlug).catch((): null => null);
      return read?.onboardingSentAt ?? sentAt ?? new Date().toISOString();
    }
  }

  // Neither helper below ever meets a `stringList` field: `children`'s own panel renders
  // `ChildrenSection` directly instead of calling `fields()`, so `Field` (text, date or a
  // textarea) never needs to accept an array value.
  const scalar = (v: string | boolean | string[] | undefined): string | boolean | undefined =>
    typeof v === 'string' || typeof v === 'boolean' ? v : undefined;

  const fields = (section: OnboardingFormSection, filter?: (k: string) => boolean) =>
    ONBOARDING_FORM_FIELDS[section].filter((f) => f.kind !== 'yesNo' && f.kind !== 'stringList' && !ONBOARDING_TRUSTED_CHOICE_KEYS.includes(f.key) && (!filter || filter(f.key))).map((spec) => (
      <Field key={spec.key} spec={spec} value={scalar(values[spec.key])} mark={markFor(spec.key)}
        onChange={(v) => setValue(spec.key, section, v)} />
    ));

  const anyPrefill = (section: OnboardingFormSection) => ONBOARDING_FORM_FIELDS[section].some((f) => fromPrefill.has(f.key));
  const person = (s: 'client' | 'spouse') => {
    const specs = ONBOARDING_FORM_FIELDS[s].filter((f) => f.kind !== 'yesNo');
    const before = specs.filter((f) => !/_(phone|email)$/.test(f.key));
    // The client's own email is the invited address, shown read-only (steward-app#1883); the
    // spouse's stays an ordinary field.
    const after = specs.filter((f) => /_(phone|email)$/.test(f.key) && !(s === 'client' && f.key === 'client_email'));
    const render = (list: OnboardingFieldSpec[]) => list.map((spec) => (
      <Field key={spec.key} spec={spec} value={scalar(values[spec.key])} mark={markFor(spec.key)}
        onChange={(v) => setValue(spec.key, s, v)} />
    ));
    return (
      <>
        {render(before)}
        <TaxIdField subject={s} firm={firm} firmName={displayName} identity={identityFor(s)}
          onSave={(d) => saveTaxId(s, d)} onClear={() => clearTaxId(s)} registerFlush={registerTaxIdFlush} />
        {render(after)}
        {s === 'client' && <InvitedEmail email={form.invitedEmail ?? ''} />}
      </>
    );
  };

  /**
   * The trusted contact's choice and fields (crossmarc/steward-app#1954). The choice appears only
   * while the form has an adult to offer: a spouse or partner, or a child whose date of birth
   * shows 18 or older. What is offered and what is shown selected both come from
   * `resolveTrustedContact`, so a choice that no longer holds (the child was removed, or their date
   * of birth changed) reads as "Someone else" here and everywhere else.
   */
  const trustedContactPanel = (): ReactNode => {
    const view = viewOf(values);
    const spouseName = trustedSpouseName(view);
    const childIdx = adultChildIndexes(view);
    const childNames = Array.isArray(view['children_names']) ? view['children_names'] as string[] : [];
    const resolved = resolveTrustedContact(view);
    // A child choice that no longer holds (removed, renamed, or now under 18) is NOTHING chosen:
    // no radio checked and no Someone else fields until the client picks. The form writes nothing
    // on its own (a keystroke on Your family can make the choice briefly stale), and the resolver
    // already keeps that child's phone and email away from the firm, the review and setup (#1954).
    const staleChild = parseTrustedMember(view[ONBOARDING_TRUSTED_MEMBER_KEY])?.kind === 'child' && resolved?.subject !== 'child';
    const chosen: string | null = staleChild ? null
      : resolved?.subject === 'spouse' ? 'spouse'
      : resolved?.subject === 'child' ? trustedMemberValue({ kind: 'child', index: resolved.childIndex! }) : 'other';
    const others = ONBOARDING_FORM_FIELDS.trustedContact.filter((f) => !ONBOARDING_TRUSTED_CHOICE_KEYS.includes(f.key));
    const choose = (v: string) => {
      if (v === chosen) return;
      // Each choice starts its own details, so one person's phone never lands on another.
      for (const f of others) setValue(f.key, 'trustedContact', '');
      // 'other' (not blank) so a stale child pointer is replaced; the payload never sends it.
      setValue(ONBOARDING_TRUSTED_MEMBER_KEY, 'trustedContact', v);
      const idx = parseTrustedMember(v);
      setValue(ONBOARDING_TRUSTED_MEMBER_NAME_KEY, 'trustedContact', idx?.kind === 'child' ? (childNames[idx.index] ?? '').trim() : '');
    };
    const options: Array<{ value: string; label: ReactNode }> = [];
    if (spouseName) options.push({ value: 'spouse', label: <><UserText>{spouseName}</UserText>, your spouse or partner</> });
    for (const i of childIdx) options.push({ value: trustedMemberValue({ kind: 'child', index: i }), label: <><UserText>{childNames[i]!}</UserText>, your child</> });
    // Even with no adult left, a stale pointer keeps the group so the client can pick (and so the
    // child's leftover phone and email are never shown as free text).
    if (options.length === 0 && !staleChild) return fields('trustedContact');
    options.push({ value: 'other', label: 'Someone else' });
    const first = (n: string) => n.trim().split(/\s+/)[0] ?? n;
    const spouseFirst = firstName(view, 'spouse') || spouseName;
    const spouseReachable = typeof view['spouse_phone'] === 'string' && view['spouse_phone'].trim() !== ''
      || typeof view['spouse_email'] === 'string' && view['spouse_email'].trim() !== '';
    const childFirst = resolved?.subject === 'child' ? first(resolved.name) : '';
    return (
      <>
        <fieldset className="border-none p-0 m-0 mb-4">
          <legend className={formCls.label}>Who is your trusted contact?</legend>
          <div className="flex flex-col gap-1.5 text-[13px] text-navy font-sans">
            {options.map((o) => (
              <label key={o.value} className="flex items-center gap-1.5 cursor-pointer">
                <input type="radio" name="trustedContact_member" checked={chosen === o.value} onChange={() => choose(o.value)} /><span>{o.label}</span>
              </label>
            ))}
          </div>
          <p className="text-[11px] text-mid font-sans m-0 mt-2">Children appear here once their date of birth shows they are 18 or older.</p>
        </fieldset>
        {chosen === 'spouse' && (
          <p className="text-[13px] text-mid font-sans mt-0 mb-4" data-testid="trusted-spouse-note">
            {spouseReachable
              ? <>We will share <UserText>{spouseFirst}</UserText>&apos;s phone and email from the Your family page with {firm}.</>
              : <>Add a phone number or email for <UserText>{spouseFirst}</UserText> on the Your family page so {firm} can reach them.</>}
          </p>
        )}
        {resolved?.subject === 'child' && (
          <>
            {others.filter((f) => f.key === 'trustedContact_phone' || f.key === 'trustedContact_email').map((spec) => (
              <Field key={spec.key} spec={{ ...spec, label: `${childFirst}'s ${spec.key.endsWith('phone') ? 'phone' : 'email'}` }}
                value={scalar(values[spec.key])} onChange={(v) => setValue(spec.key, 'trustedContact', v)} />
            ))}
          </>
        )}
        {chosen === 'other' && fields('trustedContact')}
      </>
    );
  };

  // The rail marks (founder decision C4): answered when the section holds any answer, started when
  // the client has opened it, otherwise not started. Completeness is Review's job, not the rail's.
  const answeredPage = (p: AnswerPage): boolean => {
    if (p === 'requests') return answeredCount(requests, requestUploads.uploads, values, filledNumbers) > 0;
    const sections: OnboardingClientFormSection[] = p === 'family' ? ['spouse', 'children'] : [p];
    const view = viewOf(values);
    return sections.some((sec) => ONBOARDING_FORM_FIELDS[sec].some(({ key }) => {
      const v = view[key];
      return typeof v === 'boolean' || (typeof v === 'string' && v.trim() !== '') || (Array.isArray(v) && v.some((x) => typeof x === 'string' && x.trim() !== ''));
    })) || (p === 'client' && identityFor('client') !== undefined) || (p === 'family' && identityFor('spouse') !== undefined);
  };
  const railSteps: RailSection[] = steps.map((s) => ({
    key: s,
    label: stepLabel(s),
    review: s === 'review',
    mark: s === 'review' ? (sentAt ? 'done' : 'notStarted') : answeredPage(s) ? 'done' : visited.has(s) || s === step ? 'started' : 'notStarted',
  }));
  let panel: ReactNode;
  let title: ReactNode = '';
  switch (step) {
    case 'client':
      title = 'Your details';
      panel = (
        <>
          {anyPrefill('client') && <p className="text-[13px] text-mid font-sans mt-0 mb-5">Check what we filled in and add the rest.</p>}
          {person('client')}
        </>
      );
      break;
    case 'requests':
      title = 'Requests from your advisor';
      panel = <InfoRequestsStep firm={firm} note={form.requestsNote} requests={requests} withdrawn={form.withdrawnRequests} api={requestUploads} values={values} onAnswer={setAnswer}
        numbers={{ token, firmWorkspaceId, states: form.accountNumbers, onClosed: () => { closedRaised.current += 1; setRequestClosed(true); }, registerFlush: registerNumberFlush }} />;
      break;
    case 'family': {
      const showSpouse = shownSections.includes('spouse');
      const showChildren = shownSections.includes('children');
      const names = Array.isArray(values['children_names']) ? values['children_names'] as string[] : [];
      const dateOfBirths = Array.isArray(values['children_dateOfBirths']) ? values['children_dateOfBirths'] as string[] : [];
      const spouseFirst = firstName(values, 'spouse');
      title = 'Your family';
      panel = (
        <>
          <p className="text-[13px] text-mid font-sans mt-0 mb-5">
            {showSpouse ? 'Your spouse or partner and your children.' : 'Your children.'}
            {(anyPrefill('spouse') || anyPrefill('children')) && ' Check what we filled in and add the rest.'}
          </p>
          {showSpouse && (
            <>
              <YesNo name="spouse_included" label="Include a spouse or partner?" value={values['spouse_included'] as boolean | undefined}
                onChange={(v) => setValue('spouse_included', 'spouse', v)} />
              {values['spouse_included'] === true && (
                <>
                  {spouseFirst && <h2 className="m-0 mb-3 font-serif text-[18px] font-normal text-navy"><UserText>{spouseFirst}</UserText>&apos;s details</h2>}
                  {person('spouse')}
                </>
              )}
            </>
          )}
          {showChildren && (
            <div className={showSpouse ? 'mt-6 border-t border-line pt-6' : ''}>
              <h2 className="m-0 font-serif text-[18px] font-normal text-navy">Children</h2>
              <p className="text-[13px] text-mid font-sans mt-1 mb-4">Optional. Add a row for each child.</p>
              <ChildrenSection names={names} dateOfBirths={dateOfBirths}
                onChange={(nextNames, nextDobs) => {
                  setValue('children_names', 'children', nextNames);
                  setValue('children_dateOfBirths', 'children', nextDobs);
                }} />
            </div>
          )}
        </>
      );
      break;
    }
    case 'address':
      title = 'Home and mailing address';
      panel = (
        <>
          {fields('address', (k) => k.startsWith('address_legal'))}
          <label className="flex items-center gap-2 text-[13px] text-navy font-sans mb-4 cursor-pointer max-sm:min-h-11">
            <input type="checkbox" className="max-sm:h-5 max-sm:w-5" checked={values['address_mailingSameAsLegal'] === true}
              onChange={(e) => setValue('address_mailingSameAsLegal', 'address', e.target.checked)} />
            My mailing address is the same
          </label>
          {values['address_mailingSameAsLegal'] !== true && fields('address', (k) => k.startsWith('address_mailing') && k !== 'address_mailingSameAsLegal')}
          {shownSections.includes('spouse') && values['spouse_included'] === true && (
            <>
              <label className="flex items-center gap-2 text-[13px] text-navy font-sans mb-4 cursor-pointer max-sm:min-h-11">
                <input type="checkbox" className="max-sm:h-5 max-sm:w-5" checked={values['address_spouseSameAsClient'] !== false}
                  onChange={(e) => setValue('address_spouseSameAsClient', 'address', e.target.checked)} />
                My spouse or partner lives at the same address
              </label>
              {values['address_spouseSameAsClient'] === false && fields('address', (k) => k.startsWith('address_spouseLegal'))}
            </>
          )}
        </>
      );
      break;
    case 'trustedContact':
      title = 'Trusted contact';
      panel = (
        <>
          <p className="text-[13px] text-mid font-sans mt-0 mb-5">
            Someone {firm} may contact if they cannot reach you, or are concerned about your wellbeing. A trusted contact cannot make
            decisions about your accounts.
          </p>
          {trustedContactPanel()}
        </>
      );
      break;
    case 'beneficiary':
      title = 'Beneficiary wishes';
      panel = (
        <>
          <p className="text-[13px] text-mid font-sans mt-0 mb-5">What your family wants. The actual designations are set at the custodian.</p>
          {fields('beneficiary')}
        </>
      );
      break;
    case 'about':
      title = 'About you';
      panel = (
        <>
          <p className="text-[13px] text-mid font-sans mt-0 mb-5">Optional. Anything here helps {firm} get to know your family.</p>
          {fields('about')}
        </>
      );
      break;
    case 'review': {
      // Optional sections as a whole (crossmarc/steward-app#1812: children joins "about") never
      // count as "not finished", same reasoning as `onboardingFormStatus.ts`.
      const reviewWho = { hints: { client: identityFor('client')?.taxIdHint, spouse: identityFor('spouse')?.taxIdHint }, invitedEmail: form.invitedEmail ?? '' };
      const opts = { spouse: shownSections.includes('spouse'), children: shownSections.includes('children') };
      const open: { page: AnswerPage; missing: string[] }[] = [];
      for (const p of pageSections) {
        if (p === 'requests') continue;
        const section = p === 'family' ? (opts.spouse ? 'spouse' : null) : p;
        if (section === null || section === 'about') continue;
        if (status(section) !== 'complete') open.push({ page: p, missing: missingFor(section, answersForStatus) });
      }
      title = 'Check your answers';
      panel = (
        <>
          <p className="text-[13px] text-mid font-sans mt-0 mb-5">
            Everything below is what {firm} will receive. Choose Edit to change anything.
          </p>
          <div className="flex flex-col gap-6 mb-5">
            {pageSections.map((p, i) => {
              const label = stepLabel(p);
              const headId = `onb-review-${p}`;
              if (p === 'requests') {
                return (
                  <section key={p} aria-labelledby={headId} data-testid="onboarding-review-requests">
                    <div className="flex items-center gap-2.5 border-b border-line pb-2">
                      <SectionIndicator mark={railSteps[i]!.mark} number={i + 1} />
                      <h2 id={headId} className="m-0 font-serif text-[17px] font-normal text-navy">{label}</h2>
                      <button type="button" aria-label={`Edit ${label}`} onClick={() => void goTo(p)}
                        className="ml-auto bg-transparent border-none p-0 cursor-pointer text-bronze-text underline text-[13px] font-sans max-sm:min-h-11 max-sm:min-w-11">Edit</button>
                    </div>
                    <dl className="m-0 mt-2 grid grid-cols-1 gap-x-4 gap-y-1 sm:grid-cols-[200px_1fr] text-[13px] font-sans">
                      {requests.map((r) => {
                        const files = requestUploads.uploads[r.id] ?? [];
                        const answer = r.kind === 'question' ? storedAnswer(values, r.id) : '';
                        const value = r.kind === 'question' ? answer
                          : r.kind === 'account-number' ? reviewNumberValue(form.accountNumbers?.find((s) => s.requestId === r.id))
                            : files.map((f) => f.title).join(', ');
                        return (
                          <div key={r.id} className="contents">
                            <dt className="text-mid mt-1 sm:mt-0"><UserText>{requestTitle(r)}</UserText></dt>
                            <dd className={`m-0 whitespace-pre-wrap break-words ${value === '' ? 'text-mid' : 'text-navy'}`}>
                              {value === '' ? (r.kind === 'question' ? 'Not answered yet' : 'Not added yet') : <UserText>{value}</UserText>}
                            </dd>
                          </div>
                        );
                      })}
                    </dl>
                  </section>
                );
              }
              const g = reviewGroup(p, viewOf(values), reviewWho, opts);
              return (
                <section key={p} aria-labelledby={headId} data-testid={`onboarding-review-${p}`}>
                  <div className="flex items-center gap-2.5 border-b border-line pb-2">
                    <SectionIndicator mark={railSteps[i]!.mark} number={i + 1} />
                    <h2 id={headId} className="m-0 font-serif text-[17px] font-normal text-navy">{label}</h2>
                    <button type="button" aria-label={`${g.empty ? 'Add' : 'Edit'} ${label}`} onClick={() => void goTo(p)}
                      className="ml-auto bg-transparent border-none p-0 cursor-pointer text-bronze-text underline text-[13px] font-sans max-sm:min-h-11 max-sm:min-w-11">
                      {g.empty ? 'Add' : 'Edit'}
                    </button>
                  </div>
                  {g.empty
                    ? <p className="text-[13px] text-mid font-sans m-0 mt-2">Nothing added. This section is optional.</p>
                    : (
                      <dl className="m-0 mt-2 grid grid-cols-1 gap-x-4 gap-y-1 sm:grid-cols-[200px_1fr] text-[13px] font-sans">
                        {g.rows.map((r) => (
                          <div key={r.label} className="contents">
                            <dt className="text-mid mt-1 sm:mt-0">{r.label}</dt>
                            <dd className={`m-0 whitespace-pre-wrap break-words ${r.value === null ? 'text-mid' : 'text-navy'}`}>
                              {r.value === null ? NOT_FILLED_IN : <UserText>{r.value}</UserText>}
                            </dd>
                          </div>
                        ))}
                      </dl>
                    )}
                </section>
              );
            })}
          </div>
          {open.length > 0 && (
            <div className="text-[13px] text-mid font-sans mb-4" data-testid="onboarding-unfinished">
              {open.map((o) => {
                // "Their Social Security number" does not say whose, so name the spouse or partner
                // in a sentence of their own; whatever else the page is missing keeps its list.
                const ssnMissing = o.page === 'family' && o.missing.includes(SPOUSE_SSN_MISSING);
                const rest = ssnMissing ? o.missing.filter((m) => m !== SPOUSE_SSN_MISSING) : o.missing;
                return (
                  <Fragment key={o.page}>
                    {rest.length > 0 && <p className="m-0 mb-1">{stepLabel(o.page)} still needs {rest.join(', ')}.</p>}
                    {ssnMissing && (
                      <p className="m-0 mb-1">
                        <UserText>{ssnNeededSentence([firstName(values, 'spouse') || 'Your spouse or partner'])}</UserText>
                      </p>
                    )}
                  </Fragment>
                );
              })}
              <p className="m-0 mt-2">You can send now and add the rest later, or finish them first.</p>
            </div>
          )}
          {sentAt ? (
            <p className="text-[13px] text-navy font-sans m-0 mb-4" data-testid="onboarding-sent">
              Sent to {firm} on {fmtDate(sentAt)}. You can still update your answers.
            </p>
          ) : null}
          {sentAt && changesSaved ? (
            <p role="status" className="text-[13px] text-navy font-sans m-0 mb-4" data-testid="onboarding-changes-saved">
              Your changes are saved.
            </p>
          ) : null}
          {sendError && <p className={formCls.error} role="alert">{sendError}</p>}
        </>
      );
      break;
    }
  }

  if (showingRequests) {
    const { added } = splitRequests(requests, requestUploads.uploads, values, filledNumbers);
    return (
      <RequestsPage
        firmName={displayName} requests={requests} withdrawn={form.withdrawnRequests} note={form.requestsNote}
        api={requestUploads} values={values} onAnswer={setAnswer} addedCount={added.length}
        numbers={{ token, firmWorkspaceId, states: form.accountNumbers, onClosed: () => { closedRaised.current += 1; setRequestClosed(true); }, registerFlush: registerNumberFlush }}
        requestClosedNote={requestClosed ? REQUEST_CLOSED_NOTE : undefined}
        onDone={saveEverything}
        formIsOpen={sentAt === null}
        onOpenForm={() => setView('form')}
        // "Your documents are with" only when the client added something; otherwise the form's own outcome.
        onSignOut={onSignOut && (() => onSignOut(added.length > 0 ? { sent: sentAt !== null, requests: true } : { sent: sentAt !== null }))}
        justSent={justSent}
      />
    );
  }

  if (showingCompleted) {
    const { open, added } = splitRequests(requests, requestUploads.uploads, values, filledNumbers);
    return <OnboardingCompleted firmName={displayName} onReview={() => setView('form')} onSignOut={onSignOut && (() => onSignOut({ sent: true }))} sentAt={sentAt} openRequests={open} addedRequests={added} withdrawnRequests={form.withdrawnRequests} onOpenRequests={() => setView('requests')} />;
  }

  const idx = steps.indexOf(step);
  const clientName = fullName(values, 'client');
  // The client's name and Sign out: in the header from `lg`, at the foot of the section sheet below it.
  const who = (
    <>
      {clientName && <span className="text-[12px] text-navy font-sans"><UserText>{clientName}</UserText></span>}
      <LeaveControl onSignOut={onSignOut && (() => onSignOut({ sent: sentAt !== null }))} />
    </>
  );
  const saveStatus: SaveStatus = saveState.kind === 'error'
    ? { kind: 'error', text: `Not saved. ${saveState.message}` }
    : saveState.kind === 'idle' ? { kind: 'idle' } : saveState;
  return (
    <div className="min-h-screen bg-cream" data-testid="onboarding-form">
      <FormShell
        mark={
          <FirmWordmark>{displayName ? <UserText>{displayName}</UserText> : 'Steward'}</FirmWordmark>
        }
        suffix={firmName ? 'with Steward' : undefined}
        save={saveStatus}
        onRetry={() => void flush()}
        right={clientName || onSignOut || backToApp ? who : undefined}
        sheetFooter={clientName || onSignOut || backToApp ? who : undefined}
        sections={railSteps}
        showStarted
        currentKey={step}
        words={ONBOARDING_MARK_WORDS}
        onSelect={(key) => void goTo(key as Page)}
      >
        <SectionCard
          eyebrow={step === 'review' ? 'Review and send' : `Section ${pageSections.indexOf(step as AnswerPage) + 1} of ${pageSections.length}`}
          title={title}
          headingRef={headingRef}
        >
          {requestClosed && <p role="status" className="mt-4 mb-0 text-[13px] text-navy font-sans" data-testid="onb-req-closed">{REQUEST_CLOSED_NOTE}</p>}
          <div className="mt-4">
            {/* Once per form, on its first page, and only while an advisor-entered answer stands (steward-app#2770). */}
            {(step === noticePage.current || pageHoldsStaffMark(step)) && <ClientStaffNotice firmName={displayName} marks={staffAt} since={record.staffEntryConsentAt} />}
            {step === 'family' && staffAt.has('children_names') && (
              <p data-testid="client-staff-mark" className="mt-0 mb-4 text-[12px] text-slate font-sans">{`Children: ${markFor('children_names')}`}</p>
            )}
            {panel}
          </div>
          <FormFooter
            back={{ onClick: () => void goTo(steps[idx - 1]!), disabled: idx === 0 }}
            primary={step === 'review'
              ? (sentAt
                ? { label: sending ? 'Saving...' : 'Save my changes', disabled: sending, onClick: () => void saveChanges() }
                : { label: sending ? 'Sending...' : <>Send to {firm}</>, disabled: sending, onClick: () => void send() })
              : { label: 'Continue', onClick: () => void goTo(steps[idx + 1]!) }}
          />
        </SectionCard>
      </FormShell>
    </div>
  );
}
